/**
 * CR5c — DynamicTable row capability preview helpers
 *
 * Batch-resolve via CR4 API when available.
 * API failure path is deny/skeleton-safe: read fail-open, mutate fail-closed
 * (CAPABILITY_RESOLVER_SSOT §6). Local permissionKeys map is not used as API-failure
 * replacement in useCapabilityPreview.
 */

import type { UniversalBaseEntity } from '@shared/types/entity';
import type { CapabilityDecision } from '@shared/capability';
import { allowDecision, denyDecision } from '@shared/capability';
import {
  isWorkbenchCapabilityPreviewAction,
  resolveCapabilityPreviewLocal,
  resolveWorkbenchActions,
  type CapabilityPreviewInput,
  type WorkbenchResolvedAction,
} from '@shared/business-workbench';
import type { PathPlane } from '@shared/tenant-host/pathPlanes';
import type { UserRole } from '@shared/types/roles';
import type { TableUserPermissionContext } from '../../../types';
import {
  checkPermission,
  type PermissionCheckInput,
  type PermissionCheckResult,
} from '../../../utils/permissionCheck';
import {
  CAPABILITY_BATCH_RESOLVE_MAX,
  fetchCapabilityBatchResolve,
  type CapabilityResolveContext,
} from '../../../../../../services/capabilityApi';

/** Row CRUD actions resolved through capability preview */
export const ROW_CAPABILITY_ACTIONS = ['read', 'create', 'update', 'delete'] as const;

export type RowCapabilityAction = (typeof ROW_CAPABILITY_ACTIONS)[number];

export type GetRowCapabilityFn = (
  action: RowCapabilityAction,
  row: UniversalBaseEntity
) => CapabilityDecision;

/**
 * CR4 batch-resolve chunk size (CAPABILITY_BATCH_RESOLVE_MAX, default 50).
 * Wide tables: `fetchRowCapabilityPreviewMap` slices contexts into chunks of this size
 * (domain create + 3 row actions × N rows). Tables with >16 visible rows may exceed one
 * chunk — chunking is automatic; callers must not assume a single API round-trip.
 */
export const CAPABILITY_BATCH_MAX_CONTEXTS = CAPABILITY_BATCH_RESOLVE_MAX;

/** Matrix domain for approval-inbox row adapter (H-04 · H-12 · surface table-row). */
export const APPROVAL_TABLE_ROW_DOMAIN = 'flow_execution';

export interface ApprovalRowLike {
  id?: string;
  flowId: string;
  assignedToEntityId?: string;
  status?: string;
  name?: string;
  assignedAt?: string;
  operationExecutionId?: string;
}

/** Lightweight TaskAssignment row → UniversalBaseEntity for resolveTableRowWorkbenchActions. */
export function toApprovalWorkbenchRecord(
  row: ApprovalRowLike,
  tenantId: string
): UniversalBaseEntity {
  const id = row.id ?? `${row.flowId}:${row.assignedToEntityId ?? 'pending'}`;
  const assignedAt = row.assignedAt ? new Date(row.assignedAt) : new Date();
  return {
    id,
    tenantId,
    name: row.name ?? row.flowId,
    status: 'pending_approval',
    businessDomain: APPROVAL_TABLE_ROW_DOMAIN,
    createdAt: assignedAt,
    updatedAt: assignedAt,
  } as unknown as UniversalBaseEntity;
}

/** Approval process (승인/검토) gate — same edit/update action id as progressive table-row. */
export function resolveApprovalRowProcessPermission(
  row: ApprovalRowLike,
  tenantId: string,
  role: UserRole,
  permissionKeys: readonly string[],
  getRowCapability?: GetRowCapabilityFn
): PermissionCheckResult {
  const record = toApprovalWorkbenchRecord(row, tenantId);
  const actions = resolveTableRowWorkbenchActions({
    record,
    businessDomain: APPROVAL_TABLE_ROW_DOMAIN,
    role,
    permissionKeys,
    ...(getRowCapability !== undefined ? { getRowCapability } : {}),
  });
  return kernelActionPermission(
    actions,
    'edit',
    APPROVAL_TABLE_ROW_DOMAIN,
    'update',
    record,
    getRowCapability
  );
}

export function buildRowCapabilityCacheKey(
  objectId: string | undefined,
  action: RowCapabilityAction
): string {
  return `${objectId ?? '__domain__'}:${action}`;
}

export function buildBatchContextsForRows(
  businessDomain: string,
  rowIds: string[],
  plane = 'business'
): CapabilityResolveContext[] {
  const contexts: CapabilityResolveContext[] = [];

  contexts.push({
    action: 'create',
    domain: businessDomain,
    plane,
  });

  for (const objectId of rowIds) {
    for (const action of ['read', 'update', 'delete'] as const) {
      contexts.push({
        action,
        domain: businessDomain,
        plane,
        objectId,
      });
    }
  }

  return contexts;
}

/** Approval inbox rows — include flow/operation scope for AssignmentAdapter (OC-1B). */
export function buildApprovalRowBatchContexts(
  rows: readonly ApprovalRowLike[],
  businessDomain = APPROVAL_TABLE_ROW_DOMAIN,
  plane: PathPlane = 'business'
): CapabilityResolveContext[] {
  return rows.map((row) => {
    const objectId = row.id ?? `${row.flowId}:${row.assignedToEntityId ?? 'pending'}`;
    return {
      action: 'update',
      domain: businessDomain,
      plane,
      objectId,
      flowExecutionId: row.flowId,
      ...(row.operationExecutionId?.trim()
        ? { operationExecutionId: row.operationExecutionId.trim() }
        : {}),
    };
  });
}

export interface TableRowWorkbenchKernelInput {
  record: UniversalBaseEntity;
  businessDomain: string;
  role: UserRole;
  plane?: PathPlane;
  permissionKeys?: readonly string[];
  getRowCapability?: GetRowCapabilityFn;
}

/** Shared CR5c bridge — progressive + legacy table surfaces use the same contract. */
export function buildResolvePreviewDecision(
  getRowCapability: GetRowCapabilityFn | undefined,
  record: UniversalBaseEntity
): ((input: CapabilityPreviewInput) => CapabilityDecision | undefined) | undefined {
  if (!getRowCapability) {
    return undefined;
  }
  return (input: CapabilityPreviewInput) => {
    if (isWorkbenchCapabilityPreviewAction(input.action)) {
      return getRowCapability(input.action, record);
    }
    return undefined;
  };
}

/** resolveWorkbenchActions wrapper for manifest / legacy table row menus (K3). */
export function resolveTableRowWorkbenchActions(
  input: TableRowWorkbenchKernelInput
): WorkbenchResolvedAction[] {
  const {
    record,
    businessDomain,
    role,
    plane = 'business',
    permissionKeys,
    getRowCapability,
  } = input;
  const kernelRecord = {
    ...record,
    businessDomain: record.businessDomain ?? businessDomain,
  };
  const keys = permissionKeys ?? [];
  const resolvePreviewDecision = buildResolvePreviewDecision(getRowCapability, kernelRecord);

  return resolveWorkbenchActions(
    kernelRecord,
    {
      role,
      status: String(record.status),
      plane,
      surface: 'table-row',
      businessDomain,
      ...(keys.length > 0 ? { permissionKeys: [...keys] } : {}),
    },
    {
      ...(keys.length > 0 ? { permissionKeys: keys } : {}),
      ...(resolvePreviewDecision !== undefined ? { resolvePreviewDecision } : {}),
    }
  );
}

export function kernelActionPermission(
  actions: readonly WorkbenchResolvedAction[],
  actionId: string,
  domain: string,
  legacyAction: RowCapabilityAction,
  record: UniversalBaseEntity,
  getRowCapability?: GetRowCapabilityFn
): PermissionCheckResult {
  const action = actions.find((candidate) => candidate.id === actionId);
  if (action) {
    const missingKey = `${domain}:${action.permissionKey ?? legacyAction}`;
    return {
      allowed: !action.forbidden,
      missingKey,
      ...(action.forbidden && action.tooltip ? { reason: action.tooltip } : {}),
    };
  }
  if (getRowCapability) {
    return decisionToPermissionResult(
      getRowCapability(legacyAction, record),
      `${domain}:${legacyAction}`
    );
  }
  return { allowed: false, missingKey: `${domain}:${legacyAction}` };
}

export function collectTablePermissionKeys(
  userPermissionContext: TableUserPermissionContext,
  businessDomain: string
): string[] {
  const keys: string[] = [];
  for (const dp of userPermissionContext.domainPermissions) {
    if (dp.domain === businessDomain) {
      keys.push(...dp.permissions.map((permission) => String(permission)));
    }
  }
  keys.push(...userPermissionContext.globalPermissions.map((permission) => String(permission)));
  return keys;
}

export function buildLocalRowCapabilityPreviewMap(
  businessDomain: string,
  rowIds: string[],
  permissionKeys: readonly string[],
  plane = 'business'
): Record<string, CapabilityDecision> {
  // Local-only utility for explicit fallback/debug flows and helper-level tests.
  const contexts = buildBatchContextsForRows(businessDomain, rowIds, plane);
  const decisions = resolveCapabilityPreviewLocal(contexts, permissionKeys);
  const previewByKey: Record<string, CapabilityDecision> = {};

  contexts.forEach((ctx, index) => {
    const action = ctx.action as RowCapabilityAction;
    const cacheKey = buildRowCapabilityCacheKey(ctx.objectId, action);
    const decision = decisions[index];
    if (decision) {
      previewByKey[cacheKey] = decision;
    }
  });

  return previewByKey;
}

export function permissionResultToDecision(
  result: PermissionCheckResult,
  grantedOverride?: boolean
): CapabilityDecision {
  if (grantedOverride || result.allowed) {
    return allowDecision('domain');
  }
  return denyDecision({
    reason: result.reason ?? `권한 ${result.missingKey}가 필요합니다.`,
    requiredPermissions: [result.missingKey],
    source: 'domain',
    sensitive: false,
  });
}

export function decisionToPermissionResult(
  decision: CapabilityDecision,
  missingKey: string
): PermissionCheckResult {
  return {
    allowed: decision.allowed,
    missingKey,
    ...(decision.allowed ? {} : { reason: decision.reason }),
  };
}

/**
 * When capability preview API failed: read fail-open, mutate fail-closed.
 */
export function applyApiFailurePolicy(
  action: RowCapabilityAction,
  fallback: PermissionCheckResult,
  grantedOverride?: boolean
): CapabilityDecision {
  if (grantedOverride) {
    return allowDecision('composite');
  }

  if (action === 'read') {
    return allowDecision('composite');
  }

  return denyDecision({
    reason: fallback.reason ?? `권한 ${fallback.missingKey}가 필요합니다.`,
    requiredPermissions: [fallback.missingKey],
    source: 'composite',
    sensitive: false,
  });
}

export function mergePreviewDecision(
  preview: CapabilityDecision,
  fallback: PermissionCheckResult,
  action: RowCapabilityAction,
  apiFailed: boolean,
  grantedOverride?: boolean
): CapabilityDecision {
  if (grantedOverride) {
    return allowDecision(preview.source);
  }

  // Local fallback map (domain source) remains authoritative after API failure.
  if (apiFailed && preview.source !== 'domain') {
    return applyApiFailurePolicy(action, fallback, grantedOverride);
  }

  return preview;
}

export function buildPermissionFallbackInput(
  userPermissionContext: TableUserPermissionContext,
  businessDomain: string,
  action: RowCapabilityAction,
  readOnly?: boolean
): PermissionCheckInput {
  return {
    userPermissionContext,
    businessDomain,
    action,
    ...(readOnly !== undefined ? { readOnly } : {}),
  };
}

function resolveSingleLocalPreview(
  action: RowCapabilityAction,
  row: UniversalBaseEntity,
  businessDomain: string,
  permissionKeys: readonly string[]
): CapabilityDecision {
  const [decision] = resolveCapabilityPreviewLocal(
    [
      {
        action,
        domain: businessDomain,
        ...(action === 'create' ? {} : { objectId: row.id }),
      },
    ],
    permissionKeys
  );
  return decision ?? allowDecision('domain');
}

export function resolveRowCapabilityLocally(
  action: RowCapabilityAction,
  row: UniversalBaseEntity,
  input: {
    userPermissionContext: TableUserPermissionContext;
    businessDomain: string;
    readOnly?: boolean;
    grantedPermissionKeys?: Set<string>;
    previewByKey: Record<string, CapabilityDecision>;
    apiFailed: boolean;
    permissionKeys?: readonly string[];
  }
): CapabilityDecision {
  const missingKey = `${input.businessDomain}:${action}`;
  const grantedOverride = input.grantedPermissionKeys?.has(missingKey) ?? false;
  const fallback = checkPermission(
    buildPermissionFallbackInput(
      input.userPermissionContext,
      input.businessDomain,
      action,
      input.readOnly
    )
  );

  const cacheKey = buildRowCapabilityCacheKey(action === 'create' ? undefined : row.id, action);
  const preview = input.previewByKey[cacheKey];

  if (preview) {
    return mergePreviewDecision(preview, fallback, action, input.apiFailed, grantedOverride);
  }

  if (input.apiFailed) {
    return applyApiFailurePolicy(action, fallback, grantedOverride);
  }

  const permissionKeys =
    input.permissionKeys ??
    collectTablePermissionKeys(input.userPermissionContext, input.businessDomain);

  return resolveSingleLocalPreview(action, row, input.businessDomain, permissionKeys);
}

export async function fetchApprovalRowCapabilityPreviewMap(
  tenantId: string,
  rows: readonly ApprovalRowLike[],
  businessDomain = APPROVAL_TABLE_ROW_DOMAIN
): Promise<Record<string, CapabilityDecision>> {
  if (rows.length === 0) {
    return {};
  }

  const contexts = buildApprovalRowBatchContexts(rows, businessDomain);
  const previewByKey: Record<string, CapabilityDecision> = {};

  for (let offset = 0; offset < contexts.length; offset += CAPABILITY_BATCH_MAX_CONTEXTS) {
    const chunk = contexts.slice(offset, offset + CAPABILITY_BATCH_MAX_CONTEXTS);
    const decisions = await fetchCapabilityBatchResolve(tenantId, chunk);

    chunk.forEach((ctx, index) => {
      const action = ctx.action as RowCapabilityAction;
      const cacheKey = buildRowCapabilityCacheKey(ctx.objectId, action);
      const decision = decisions[index];
      if (decision) {
        previewByKey[cacheKey] = decision;
      }
    });
  }

  return previewByKey;
}

export async function fetchRowCapabilityPreviewMap(
  tenantId: string,
  businessDomain: string,
  rowIds: string[]
): Promise<Record<string, CapabilityDecision>> {
  const contexts = buildBatchContextsForRows(businessDomain, rowIds);
  const previewByKey: Record<string, CapabilityDecision> = {};

  for (let offset = 0; offset < contexts.length; offset += CAPABILITY_BATCH_MAX_CONTEXTS) {
    const chunk = contexts.slice(offset, offset + CAPABILITY_BATCH_MAX_CONTEXTS);
    const decisions = await fetchCapabilityBatchResolve(tenantId, chunk);

    chunk.forEach((ctx, index) => {
      const action = ctx.action as RowCapabilityAction;
      const cacheKey = buildRowCapabilityCacheKey(ctx.objectId, action);
      const decision = decisions[index];
      if (decision) {
        previewByKey[cacheKey] = decision;
      }
    });
  }

  return previewByKey;
}
